<template>
  <dashboard-layout :loading="initialLoading" loading-text="正在加载角色卡">
    <div class="rc-page">
      <div class="page-head">
        <div class="title-block">
          <h2>角色卡</h2>
          <p>角色卡是伴侣的人格模板，可复用创建多个伴侣，也可生成私享链接分享给好友</p>
        </div>
        <el-button type="primary" round class="create-btn" @click="openEditor()">+ 新建角色卡</el-button>
      </div>

      <div v-if="!cards.length" class="empty-tip">还没有角色卡，先创建一张吧。</div>
      <div class="grid">
        <div v-for="rc in cards" :key="rc.id" class="card">
          <div class="cover">
            <img :src="rc.cover || require('@/assert/主页人物.png')" alt="" />
            <span class="source" :class="{ imported: rc.source === '导入' }">{{ rc.source }}</span>
            <span v-if="rc.shared" class="shared"><i class="el-icon-share"></i> 已分享</span>
            <span v-if="rc.referenceStatus && rc.referenceStatus !== 'approved'" class="audit" :class="rc.referenceStatus">
              {{ auditLabel(rc.referenceStatus) }}
            </span>
          </div>
          <div class="body">
            <h3>{{ rc.name }}</h3>
            <p class="desc">{{ rc.desc }}</p>
            <div class="tags">
              <span v-for="t in rc.tags" :key="t">{{ t }}</span>
            </div>
            <div class="foot">
              <small>更新于 {{ rc.updatedAt }}</small>
            </div>
            <div class="btns">
              <el-button type="primary" size="small" round @click="createCompanion(rc)">用这张卡创建伴侣</el-button>
              <el-button v-if="canEdit(rc)" size="small" round @click="openEditor(rc)">编辑</el-button>
              <el-dropdown v-if="hasMoreActions(rc)" trigger="click" @command="(cmd) => onCommand(cmd, rc)">
                <el-button size="small" circle icon="el-icon-more" />
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="canShare(rc)" command="share"><i class="el-icon-share"></i> 生成私享链接</el-dropdown-item>
                  <el-dropdown-item v-if="canShare(rc) && rc.shared" command="unshare"><i class="el-icon-circle-close"></i> 撤销分享</el-dropdown-item>
                  <el-dropdown-item v-if="canEdit(rc)" command="reference"><i class="el-icon-picture-outline"></i> 更换参考图</el-dropdown-item>
                  <el-dropdown-item v-if="canDelete(rc)" command="delete" divided><span class="del-text">删除角色卡</span></el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>
      <input ref="refImageInput" type="file" accept="image/*" style="display: none" @change="onReferenceFile" />
    </div>

    <!-- 私享分享 -->
    <el-dialog title="生成私享链接" :visible.sync="shareVisible" width="440px" custom-class="yw-dialog">
      <div class="share-body" v-if="shareTarget">
        <p class="share-hint">分享链接使用高强度随机 token，不可猜测；好友可凭链接把这张角色卡复制到自己账号。</p>
        <el-form label-position="top">
          <el-form-item label="分享标题">
            <el-input v-model="shareForm.title" />
          </el-form-item>
          <el-form-item label="分享文案">
            <el-input type="textarea" :rows="2" v-model="shareForm.message" />
          </el-form-item>
          <el-form-item label="有效期">
            <el-select v-model="shareForm.expires" style="width: 100%">
              <el-option label="7 天" value="7d" />
              <el-option label="30 天" value="30d" />
              <el-option label="永久有效" value="never" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-checkbox v-model="shareForm.allowImport">允许对方导入到自己的角色卡</el-checkbox>
          </el-form-item>
        </el-form>
        <div v-if="!shareLink" class="gen-row">
          <el-button type="primary" round @click="generateShare">生成私享链接</el-button>
        </div>
        <div v-else class="link-row">
          <el-input :value="shareLink" readonly />
          <el-button type="primary" round @click="copyLink">复制链接</el-button>
        </div>
        <p v-if="shareLink" class="warn"><i class="el-icon-warning-outline"></i> 原始 token 仅展示一次，请妥善保存</p>
      </div>
      <span slot="footer">
        <el-button round @click="shareVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </dashboard-layout>
</template>

<script>
import { getUser, roleCardApi, shareApi } from "@/services/api";
import DashboardLayout from "@/components/DashboardLayout.vue";

export default {
  name: "RoleCardsView",
  components: { DashboardLayout },
  data() {
    return {
      cards: [],
      sharedIds: [],
      myShares: [],
      referenceTarget: null,
      shareVisible: false,
      shareTarget: null,
      shareForm: { title: "我没时间，创建个自己来陪你", message: "", expires: "30d", allowImport: true },
      shareLink: "",
      initialLoading: true,
    };
  },
  created() {
    this.load();
  },
  methods: {
    isOwnedRoleCard(rc) {
      const user = getUser();
      const raw = rc.raw || {};
      return !raw.is_system && !!user && String(raw.owner_user_id) === String(user.id);
    },
    canEdit(rc) {
      const user = getUser();
      if (rc.raw && rc.raw.is_system) return !!(user && user.is_admin);
      return this.isOwnedRoleCard(rc);
    },
    canShare(rc) {
      return this.isOwnedRoleCard(rc);
    },
    canDelete(rc) {
      return this.isOwnedRoleCard(rc);
    },
    hasMoreActions(rc) {
      return this.canShare(rc) || this.canEdit(rc) || this.canDelete(rc);
    },
    async load() {
      try {
        const [list, shares] = await Promise.all([roleCardApi.list(), shareApi.myShares().catch(() => [])]);
        this.myShares = (shares || []).filter((s) => !s.revoked_at);
        this.sharedIds = this.myShares.map((s) => s.role_card_id);
        this.cards = (list || []).map((rc) => ({
          id: rc.id,
          name: rc.name,
          desc: rc.description || "暂无描述",
          tags: [],
          cover: rc.reference_image_url || "",
          prompt: rc.prompt,
          referenceStatus: rc.reference_image_safety_status || "unknown",
          source: rc.is_system && rc.access_mode === "allowlist" ? "内测" : rc.is_system ? "系统" : "原创",
          usedCount: "-",
          shared: this.sharedIds.includes(rc.id),
          updatedAt: (rc.updated_at || "").slice(0, 10),
          raw: rc,
        }));
      } catch (e) {
        this.cards = [];
        this.$message.error(e.message || "加载角色卡失败");
      } finally {
        this.initialLoading = false;
      }
    },
    openEditor(rc) {
      const query = {};
      if (rc) query.id = rc.id;
      this.$router.push({ path: "/role-card-editor", query });
    },
    createCompanion(rc) {
      this.$prompt("给新伴侣起个名字", `用「${rc.name}」创建伴侣`, {
        inputValue: rc.name,
        confirmButtonText: "创建",
        cancelButtonText: "取消",
        inputValidator: (v) => (!!(v || "").trim() ? true : "名称不能为空"),
      })
        .then(async ({ value }) => {
          try {
            const companion = await roleCardApi.createCompanion(rc.id, {
              name: value.trim(),
              description: rc.raw ? rc.raw.description || "" : "",
              role_card_id: rc.id,
            });
            this.$message.success(`伴侣「${companion.name}」已创建`);
            this.$router.push({ path: "/companion-detail", query: { id: companion.id } });
          } catch (e) {
            this.$message.error(e.message || "创建失败");
          }
        })
        .catch(() => {});
    },
    onCommand(cmd, rc) {
      if (cmd === "share") this.openShare(rc);
      else if (cmd === "unshare") this.revokeShare(rc);
      else if (cmd === "reference") this.pickReference(rc);
      else if (cmd === "delete") this.confirmDelete(rc);
    },
    pickReference(rc) {
      this.referenceTarget = rc;
      this.$refs.refImageInput.value = "";
      this.$refs.refImageInput.click();
    },
    async onReferenceFile(e) {
      const file = e.target.files && e.target.files[0];
      const rc = this.referenceTarget;
      if (!file || !rc) return;
      try {
        const uploaded = await roleCardApi.uploadReference(file);
        const raw = rc.raw || {};
        await roleCardApi.update(rc.id, {
          name: raw.name || rc.name,
          description: raw.description || "",
          prompt: raw.prompt || rc.prompt,
          prompt_sections: raw.prompt_sections || {},
          reference_image_url: uploaded.reference_image_url,
          grok_reference_image_url: uploaded.grok_reference_image_url || raw.grok_reference_image_url || "",
          reference_image_safety_status: uploaded.safety_status || "unknown",
          reference_image_safety_message: uploaded.safety_message || "",
        });
        this.$message.success("参考图已更新");
        this.load();
      } catch (err) {
        this.$message.error(err.message || "上传失败");
      }
    },
    revokeShare(rc) {
      const share = this.myShares.find((s) => s.role_card_id === rc.id);
      if (!share) {
        this.$message.warning("没有找到生效中的分享");
        return;
      }
      this.$confirm(`撤销「${rc.name}」的分享链接？已导入的角色卡不受影响。`, "撤销分享", { type: "warning" })
        .then(async () => {
          try {
            await shareApi.revoke(share.id);
            this.myShares = this.myShares.filter((s) => s.id !== share.id);
            rc.shared = false;
            this.$message.success("已撤销分享");
          } catch (e) {
            this.$message.error(e.message || "撤销失败");
          }
        })
        .catch(() => {});
    },
    auditLabel(status) {
      return { pending: "未审核", rejected: "不可用", unknown: "未知" }[status] || status;
    },
    openShare(rc) {
      this.shareTarget = rc;
      this.shareForm = { title: "我没时间，创建个自己来陪你", message: `这是我精心调教的${rc.name}，希望也能温暖到你～`, expires: "30d", allowImport: true };
      this.shareLink = "";
      this.shareVisible = true;
    },
    async generateShare() {
      if (!this.shareTarget) return;
      const expiresMap = { "7d": this.daysFromNow(7), "30d": this.daysFromNow(30), never: "" };
      try {
        const res = await shareApi.create(this.shareTarget.id, {
          title: this.shareForm.title,
          message: this.shareForm.message,
          allow_import: this.shareForm.allowImport,
          expires_at: expiresMap[this.shareForm.expires] || "",
        });
        this.shareLink = res.share_url || location.origin + "/#/share/" + res.token;
        this.shareTarget.shared = true;
        this.$message.success("私享链接已生成");
      } catch (e) {
        this.$message.error(e.message || "生成失败");
      }
    },
    daysFromNow(d) {
      const t = new Date(Date.now() + d * 86400000);
      return t.toISOString().slice(0, 10);
    },
    copyLink() {
      const input = document.createElement("input");
      input.value = this.shareLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      this.$message.success("链接已复制");
    },
    confirmDelete(rc) {
      this.$confirm(`确定删除角色卡「${rc.name}」吗？已用此卡创建的伴侣不受影响。`, "删除角色卡", {
        type: "warning",
        confirmButtonText: "确定删除",
        cancelButtonText: "取消",
      })
        .then(async () => {
          try {
            await roleCardApi.remove(rc.id);
            this.cards = this.cards.filter((c) => c.id !== rc.id);
            this.$message.success("已删除");
          } catch (e) {
            this.$message.error(e.message || "删除失败");
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped lang="scss">
.rc-page {
  .empty-tip {
    padding: 60px 0;
    text-align: center;
    color: #bbb;
    font-size: 14px;
  }

  .page-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;

    .title-block {
      h2 {
        margin: 0 0 6px;
        font-size: 26px;
        font-weight: 700;
        color: #1a1a1a;
      }

      p {
        margin: 0;
        max-width: 560px;
        color: #999;
        font-size: 13px;
        line-height: 1.6;
      }
    }

    .create-btn {
      flex-shrink: 0;
      border: 0;
      background: linear-gradient(90deg, #c94225, #7255e9);
    }
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 18px;

    .card {
      position: relative;
      aspect-ratio: 0.618 / 1;
      border-radius: 18px;
      background: #eeeafe;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
      overflow: hidden;
      transition: transform 0.25s, box-shadow 0.25s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(114, 85, 233, 0.16);
      }

      .cover {
        position: absolute;
        inset: 0;
        height: auto;
        background: linear-gradient(180deg, #eeeafe, #ded6ff);

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
        }

        .source {
          position: absolute;
          left: 12px;
          top: 12px;
          padding: 3px 10px;
          border-radius: 10px;
          color: #fff;
          font-size: 11px;
          background: rgba(114, 85, 233, 0.85);

          &.imported {
            background: rgba(82, 196, 26, 0.85);
          }
        }

        .shared {
          position: absolute;
          right: 12px;
          top: 12px;
          padding: 3px 10px;
          border-radius: 10px;
          color: #fff;
          font-size: 11px;
          background: rgba(0, 0, 0, 0.35);
        }

        .audit {
          position: absolute;
          right: 12px;
          top: 42px;
          padding: 3px 10px;
          border-radius: 10px;
          color: #fff;
          font-size: 11px;
          background: rgba(250, 173, 20, 0.92);

          &.rejected {
            background: rgba(245, 34, 45, 0.9);
          }

          &.unknown {
            background: rgba(120, 120, 120, 0.82);
          }
        }
      }

      .body {
        position: absolute;
        left: 10px;
        right: 10px;
        bottom: 10px;
        padding: 12px;
        border: 1px solid rgba(255, 255, 255, 0.64);
        border-radius: 14px;
        background: rgba(255, 255, 255, 0.78);
        backdrop-filter: blur(14px);
        box-shadow: 0 12px 28px rgba(25, 10, 18, 0.12);

        h3 {
          margin: 0 0 6px;
          font-size: 17px;
          font-weight: 700;
        }

        .desc {
          margin: 0 0 10px;
          min-height: 36px;
          color: #999;
          font-size: 12px;
          line-height: 1.5;
        }

        .tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 10px;

          span {
            padding: 2px 10px;
            border-radius: 10px;
            color: #816bef;
            font-size: 11px;
            background: #f7f4ff;
          }
        }

        .foot {
          margin-bottom: 12px;

          small {
            color: #bbb;
            font-size: 11px;
          }
        }

        .btns {
          display: flex;
          gap: 6px;
          align-items: center;
          flex-wrap: wrap;

          .el-button--primary {
            border: 0;
            background: linear-gradient(90deg, #c94225, #7255e9);
          }
        }
      }
    }
  }
}

.share-body {
  .share-hint {
    margin: 0 0 14px;
    padding: 10px 12px;
    border-radius: 10px;
    color: #816bef;
    font-size: 12px;
    line-height: 1.5;
    background: #f8f6fb;
  }

  .link-row {
    display: flex;
    gap: 8px;
    margin-top: 6px;

    .el-input {
      flex: 1;
    }

    .el-button {
      border: 0;
      background: linear-gradient(90deg, #c94225, #7255e9);
    }
  }

  .warn {
    margin: 12px 0 0;
    color: #faad14;
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .rc-page {
    .page-head {
      flex-wrap: wrap;
      gap: 10px;
    }

    .grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
  }
}
</style>

<style scoped lang="scss">
.rc-page {
  min-width: 0;

  .empty-tip,
  .page-head .title-block p,
  .grid .card .body .desc,
  .grid .card .body .foot small {
    color: var(--xyo-muted);
  }

  .page-head {
    min-width: 0;

    .title-block {
      min-width: 0;

      h2 {
        color: var(--xyo-ink-strong);
      }
    }

    .create-btn {
      background: var(--xyo-primary);
    }
  }

  .grid .card {
    background: var(--xyo-surface-muted);

    &:hover {
      box-shadow: var(--xyo-shadow-lg);
    }

    .cover {
      background: var(--xyo-surface-muted);

      .source {
        background: var(--xyo-primary);

        &.imported {
          background: var(--xyo-success);
        }
      }

      .audit {
        background: var(--xyo-warning);

        &.rejected {
          background: var(--xyo-danger);
        }

        &.unknown {
          background: var(--xyo-info);
        }
      }
    }

    .body {
      border-color: var(--xyo-line);
      background: var(--xyo-surface-raised);

      .tags span {
        color: var(--xyo-primary);
        background: var(--xyo-accent-pale);
      }

      .btns .el-button--primary {
        background: var(--xyo-primary);
      }
    }
  }
}

.share-body {
  min-width: 0;

  .share-hint {
    color: var(--xyo-primary);
    border: 1px solid var(--xyo-line);
    background: var(--xyo-accent-pale);
  }

  .link-row {
    min-width: 0;

    .el-input {
      min-width: 0;
    }

    .el-button {
      background: var(--xyo-primary);
    }
  }

  .warn {
    color: var(--xyo-warning);
  }
}

@media (max-width: 768px) {
  .rc-page {
    max-width: 100%;

    .page-head,
    .page-head .title-block,
    .grid {
      min-width: 0;
      max-width: 100%;
    }

    .grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .share-body .link-row {
    flex-wrap: wrap;

    .el-input {
      flex-basis: 100%;
    }
  }
}
</style>
